/**
 * Тело таблицы: виртуализация, курсор ячейки, правка по месту (§4.3, §5).
 *
 * Плоский список строк (бренды + раскрытые категории) прокручивается через
 * `@tanstack/react-virtual`. Закреплённые колонки — CSS `position: sticky` с
 * фиксированными смещениями: ширины колонок заданы в px и в JS не считаются.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import type { Brand, ChannelCode, Conflict, SortRule } from '@shared/types.ts'
import { getPrefs } from '../../../app/prefs.js'
import type { Overlay } from '../../../lib/draft-overlay.js'
import { brandHasDraft } from '../../../lib/draft-overlay.js'
import type { BulkEditApi } from '../editing/useBulkEdit.js'
import { useCellCursor, type CellPos } from '../editing/useCellCursor.js'
import { tableTexts as t } from '../texts.table.js'
import { BrandRow, type EditPermissions, type RowActions, type TableLayout } from './BrandRow.js'
import { AddCategoryRow, CategoryRow } from './CategoryRow.js'
import type { ColumnDef, ColumnGroupCode } from './columns.js'
import { useFreshCells } from './fresh.js'
import { buildRows, isApplicable, isInChannel, rowHeight, useRowHeights, type TableRow } from './rows.js'
import { GroupHeader } from './GroupHeader.js'
import { TableHeader } from './TableHeader.js'
import { readCell, writeCell, type ValueContext, type WriteActions } from './values.js'
import './BrandTable.css'

export interface BrandTableProps {
  /** Строки снимка в порядке показа (§5.4). */
  brands: readonly Brand[]
  layout: TableLayout
  ctx: ValueContext
  overlay: Overlay
  perms: EditPermissions
  conflicts: ReadonlyMap<number, Conflict>
  revision: number
  channelName: string
  /** Число пустых полей зоны ответственности смотрящего (§4.9). */
  missingOf: (brand: Brand) => number
  /** Бренд, открытый сейчас в карточке: его строку подсвечиваем. */
  openBrandId?: number | null
  myColumn: string | null
  sort: readonly SortRule[]
  onSort: (code: string, additive: boolean) => void
  collapsedGroups: ReadonlySet<ColumnGroupCode>
  onToggleGroup: (group: ColumnGroupCode) => void
  expanded: ReadonlySet<number>
  onToggleExpanded: (brandId: number) => void
  onExpandAll: () => void
  driftIds: ReadonlySet<number>
  outsideFilterIds: ReadonlySet<number>
  bulk: BulkEditApi
  selectionEnabled: boolean
  writeActions: WriteActions
  onOpenComments: (brand: Brand) => void
  onSwitchChannel: (channel: ChannelCode) => void
  onAddToChannel: (brand: Brand, channel: ChannelCode) => void
  onStatus: (text: string) => void
  /** Что показать вместо строк, когда под фильтр ничего не подошло. */
  empty?: ReactNode
  /** Поиск по брендам: таблица ставит его в шапку блока «Бренд». */
  search?: ReactNode
  pinnedLeftExtra: string | null
  onSortDirection: (code: string, direction: 'asc' | 'desc' | null) => void
  /** Ручная ширина колонки: перетаскивание границы заголовка. */
  onResizeColumn: (code: string, width: number) => void
  onHideColumn: (code: string) => void
}

export function BrandTable(props: BrandTableProps) {
  const {
    brands, layout, ctx, overlay, perms, conflicts, revision, channelName, missingOf, openBrandId,
    myColumn, sort, onSort, collapsedGroups, onToggleGroup, expanded, onToggleExpanded,
    onExpandAll, driftIds, outsideFilterIds, bulk, selectionEnabled, writeActions,
    onOpenComments, onSwitchChannel, onAddToChannel, onStatus, empty, search,
    pinnedLeftExtra, onSortDirection, onResizeColumn, onHideColumn,
  } = props

  const scrollRef = useRef<HTMLDivElement>(null)
  /* Ширина видимой части. Плашка строки прилипает к левому краю и занимает
     ровно её, поэтому карточка остаётся в кадре со скруглёнными краями, как бы
     далеко вбок ни уехали колонки. */
  const [viewWidth, setViewWidth] = useState(0)
  useEffect(() => {
    const node = scrollRef.current
    if (!node) return
    const observer = new ResizeObserver(() => setViewWidth(node.clientWidth))
    observer.observe(node)
    setViewWidth(node.clientWidth)
    return () => observer.disconnect()
  }, [])
  const heights = useRowHeights()
  const fresh = useFreshCells(brands, revision)

  const collator = useMemo(() => new Intl.Collator('ru', { sensitivity: 'base', numeric: true }), [])
  const rows = useMemo(
    () => buildRows(brands, expanded, ctx.channel, overlay, collator),
    [brands, expanded, ctx.channel, overlay, collator],
  )

  const columns = layout.columns

  // Шапки групп и колонок лежат в том же скролле, что и тело: их высота —
  // это `scrollMargin` (координаты строк) и `scrollPaddingStart` (чтобы
  // `scrollToIndex` не загонял строку под закреплённую шапку).
  const headerHeight = heights.groupHead + heights.colHead

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => {
      const row = rows[index]
      return row ? rowHeight(row, heights) : heights.brand
    },
    overscan: 12,
    scrollMargin: headerHeight,
    scrollPaddingStart: headerHeight,
    getItemKey: (index) => rows[index]?.key ?? index,
  })

  // ── применимость, права, пустота ───────────────────────────────────────────

  const isChannelColumn = useCallback((column: ColumnDef) => (
    column.source.kind === 'channel'
    || (column.source.kind === 'custom' && column.source.scope === 'brand_channel')
  ), [])

  const isFocusable = useCallback((rowIndex: number, colIndex: number): boolean => {
    const row = rows[rowIndex]
    const column = columns[colIndex]
    if (!row || !column) return false
    /* Категории — не ячейки, а самостоятельные сущности со своими контролами,
       поэтому курсор сетки в их строки не заходит вовсе. */
    if (row.kind !== 'brand') return false
    if (column.role === 'gutter') return row.kind === 'brand' && selectionEnabled
    if (column.editor === 'none') return false
    if (!isApplicable(column, row)) return false
    if (row.kind === 'brand' && isChannelColumn(column) && !isInChannel(row.brand, ctx.channel)) return false
    return true
  }, [rows, columns, selectionEnabled, isChannelColumn, ctx.channel])

  const cellAt = useCallback((rowIndex: number, colIndex: number) => {
    const row = rows[rowIndex]
    const column = columns[colIndex]
    if (!row || !column) return null
    return { row, column, cell: readCell(ctx, column, row) }
  }, [rows, columns, ctx])

  const canEdit = useCallback((rowIndex: number, colIndex: number): boolean => {
    if (!isFocusable(rowIndex, colIndex)) return false
    const at = cellAt(rowIndex, colIndex)
    if (!at || at.column.editor === 'none') return false
    if (!perms.mayEdit) return false
    if (perms.onlyEmpty && at.cell && !at.cell.empty) return false
    return true
  }, [isFocusable, cellAt, perms])

  const isEmpty = useCallback((rowIndex: number, colIndex: number): boolean => {
    const at = cellAt(rowIndex, colIndex)
    return at?.cell?.empty ?? false
  }, [cellAt])

  const isToggle = useCallback((rowIndex: number, colIndex: number): boolean => (
    columns[colIndex]?.editor === 'bool'
  ), [columns])

  // ── запись правки ──────────────────────────────────────────────────────────

  const write = useCallback((column: ColumnDef, row: TableRow, value: unknown) => {
    void writeCell(writeActions, ctx.channel, column, row, value)
  }, [writeActions, ctx.channel])

  const toggleBool = useCallback((pos: CellPos) => {
    const at = cellAt(pos.row, pos.col)
    if (!at || !at.cell) return
    write(at.column, at.row, at.cell.value !== true)
  }, [cellAt, write])

  // ── курсор и карта клавиш ──────────────────────────────────────────────────

  const scrollToRow = useCallback((index: number) => {
    virtualizer.scrollToIndex(index, { align: 'auto' })
  }, [virtualizer])

  const rowsPerPage = Math.max(
    1,
    Math.floor((scrollRef.current?.clientHeight ?? heights.brand * 20) / heights.brand) - 1,
  )

  const cursor = useCellCursor({
    rowCount: rows.length,
    colCount: columns.length,
    isFocusable,
    canEdit,
    isEmpty,
    isToggle,
    toggleBool,
    selectRow: (rowIndex) => {
      const row = rows[rowIndex]
      if (row?.kind === 'brand') bulk.toggle(row.brand.id, { shift: false, meta: false })
    },
    expandRow: (rowIndex, all) => {
      if (all) { onExpandAll(); return }
      const row = rows[rowIndex]
      if (row) onToggleExpanded(row.brand.id)
    },
    scrollToRow,
    onNoMoreEmpty: () => onStatus(t.edit.noMoreEmpty),
    /* Правка начинается по Enter. Режим «как в Google Sheets» был настройкой
       в меню, но читался один раз при загрузке — переключение ничего не
       меняло до перезагрузки, и настройка только путала. */
    typeToEdit: false,
    rowsPerPage,
  })

  // ── действия строк ─────────────────────────────────────────────────────────

  const rowActions = useCallback((row: TableRow): RowActions => ({
    expandRow: () => onToggleExpanded(row.brand.id),
    openComments: () => onOpenComments(row.brand),
    switchChannel: (channel) => onSwitchChannel(channel),
    addToChannel: (channel) => onAddToChannel(row.brand, channel),
    expandGroup: (group) => onToggleGroup(group),
    toggleSelect: (brandId, modifiers) => bulk.toggle(brandId, modifiers),
    dragSelectStart: (brandId) => bulk.dragStart(brandId),
    dragSelectOver: (brandId) => bulk.dragOver(brandId),
    write,
  }), [onToggleExpanded, onOpenComments, onSwitchChannel, onAddToChannel, onToggleGroup, bulk, write])

  // Привязки категорий с правкой — считаем один раз на наложение, а не на строку.
  const draftCategoryIds = useMemo(() => {
    const ids = new Set<number>()
    for (const key of overlay.byKey.keys()) {
      if (!key.startsWith('catchan:')) continue
      const id = Number(key.split(':')[1])
      if (Number.isFinite(id)) ids.add(id)
    }
    for (const id of overlay.detached) ids.add(id)
    for (const id of overlay.draftCategories) ids.add(id)
    return ids
  }, [overlay])

  const items = virtualizer.getVirtualItems()


  return (
    <div className="table">
      <div className="table__scroll" ref={scrollRef}>
        <div
          className={bulk.dragging ? 'table__grid table__grid--dragging' : 'table__grid'}
          role="grid"
          aria-label={t.edit.gridLabel}
          aria-rowcount={rows.length}
          aria-colcount={columns.length}
          tabIndex={cursor.cursor ? -1 : 0}
          style={{
            width: `${layout.totalWidth}px`,
            /* Ширину всегда задаём числом: плашки строк и шапки рисуются по
               этой переменной, и запасное значение в процентах им не подходит
               — их собственная ширина нулевая. */
            ...({ '--table-view': `${viewWidth > 0 ? viewWidth : layout.totalWidth}px` } as React.CSSProperties),
          }}
          onKeyDown={cursor.handleKeyDown}
        >
          <GroupHeader
            columns={columns}
            collapsed={collapsedGroups}
            onToggle={onToggleGroup}
            leftOffset={layout.leftOffset}
            rightOffset={layout.rightOffset}
            totalWidth={layout.totalWidth}
            search={search}
          />
          <TableHeader
            columns={columns}
            leftOffset={layout.leftOffset}
            rightOffset={layout.rightOffset}
            lastLeftCode={layout.lastLeftCode}
            firstRightCode={layout.firstRightCode}
            totalWidth={layout.totalWidth}
            channelName={channelName}
            myColumn={myColumn}
            sort={sort}
            onSort={onSort}
            shownCount={brands.length}
            allSelected={bulk.allSelected}
            someSelected={bulk.someSelected}
            onToggleAll={bulk.selectAllShown}
            selectionEnabled={selectionEnabled}
            onSortDirection={onSortDirection}
            onResizeColumn={onResizeColumn}
            onHide={onHideColumn}
            onExpandAll={onExpandAll}
            pinnedLeftExtra={pinnedLeftExtra}
          />

          {/* Шапка остаётся на месте и когда строк нет: без неё человек теряет
              контекст — какие колонки он смотрел и что отфильтровано. */}
          {rows.length === 0 && empty ? <div className="table__empty">{empty}</div> : null}

          <div
            className="table__body"
            style={{ height: `${virtualizer.getTotalSize()}px`, width: `${layout.totalWidth}px` }}
          >
            {items.map((item) => {
              const row = rows[item.index]
              if (!row) return null

              if (row.kind === 'brand') {
                return (
                  <BrandRow
                    key={item.key}
                    row={row}
                    rowIndex={item.index}
                    top={item.start - headerHeight}
                    height={item.size}
                    layout={layout}
                    ctx={ctx}
                    overlay={overlay}
                    cursor={cursor}
                    perms={perms}
                    conflicts={conflicts}
                    fresh={fresh}
                    missingOf={missingOf}
                    selected={bulk.isSelected(row.brand.id)}
                    expanded={expanded.has(row.brand.id)}
                    selectionEnabled={selectionEnabled}
                    drift={driftIds.has(row.brand.id)}
                    outsideFilter={outsideFilterIds.has(row.brand.id)}
                    opened={row.brand.id === openBrandId}
                    hasDraft={brandHasDraft(overlay, row.brand)}
                    hiddenDraft={!expanded.has(row.brand.id)
                      && row.brand.categories.some((category) => draftCategoryIds.has(category.id))}
                    actions={rowActions(row)}
                    channelName={channelName}
                  />
                )
              }

              if (row.kind === 'category') {
                return (
                  <CategoryRow
                    key={item.key}
                    row={row}
                    rowIndex={item.index}
                    top={item.start - headerHeight}
                    height={item.size}
                    layout={layout}
                    ctx={ctx}
                    readOnly={!perms.mayEdit}
                  />
                )
              }

              return (
                <AddCategoryRow
                  key={item.key}
                  row={row}
                  rowIndex={item.index}
                  top={item.start - headerHeight}
                  height={item.size}
                  layout={layout}
                />
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
